import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaTrash, FaPlus, FaGripVertical, FaLock, FaBookOpen, FaCrown, FaArrowRight, FaCheck } from "react-icons/fa";
import {
  fetchCourseById,
  fetchChaptersForCourse,
  deleteChapter,
  reorderChapters,
} from "../../apis/CourseApis";
import { fetchCourseAccess } from "../../apis/AccessApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import SeoMeta from "../SeoMeta";
import { SkeletonCourseDetails } from "../Skeletons";
import { FREE_CHAPTER_COUNT, getChapterUnlockStatus } from "../../config/courseUnlock";
import { computeChapterCompletionPercent } from "../../utils/courseProgress";
import { useTheme } from "../Theme/ThemeContext";

export default function CourseDetails() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, loggedInUser } = useAuth();
  const { successToast, errorToast } = useToastHelpers();
  const { currentTheme } = useTheme();

  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chaptersOriginal, setChaptersOriginal] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingChapter, setDeletingChapter] = useState(null);
  const [draggedChapterIndex, setDraggedChapterIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [courseAccess, setCourseAccess] = useState({ allowed: false });
  const [chapterCompletionPercents, setChapterCompletionPercents] = useState([]);

  // Load course and chapters
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    
    const loadData = async () => {
      try {
        const courseData = await fetchCourseById(courseId);
        if (isMounted) setCourse(courseData);

        const chaptersData = await fetchChaptersForCourse(courseId);
        if (isMounted) {
          if (Array.isArray(chaptersData?.chapters)) {
            setChapters(chaptersData.chapters);
            setChaptersOriginal(chaptersData.chapters);
          } else if (Array.isArray(chaptersData)) {
            setChapters(chaptersData);
            setChaptersOriginal(chaptersData);
          }
        }
      } catch (err) {
        console.error("Error loading course:", err);
        if (isMounted) errorToast("Failed to load course");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();
    return () => { isMounted = false; };
  }, [courseId]);

  const handleDeleteChapter = async (chapterId) => {
    if (window.confirm("Are you sure you want to delete this chapter?")) {
      setDeletingChapter(chapterId);
      try {
        await deleteChapter(chapterId, user?.token);
        setChapters((prev) => prev.filter((c) => c.id !== chapterId));
        setChaptersOriginal((prev) => prev.filter((c) => c.id !== chapterId));
        successToast("Chapter deleted successfully!");
      } catch (err) {
        console.error("Error deleting chapter:", err);
        errorToast("Failed to delete chapter");
      } finally {
        setDeletingChapter(null);
      }
    }
  };

  const isAdmin = loggedInUser?.role === "admin";

  // Backend-driven access (paid users). If user is not logged in, assume not allowed.
  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      if (!courseId) return;
      if (!user?.token) {
        if (isMounted) setCourseAccess({ allowed: false });
        return;
      }

      const userId = user?.userId || loggedInUser?.id || loggedInUser?._id;
      if (!userId) {
        if (isMounted) setCourseAccess({ allowed: false });
        return;
      }

      try {
        const data = await fetchCourseAccess({
          courseId,
          userId,
          token: user.token,
        });

        if (isMounted) setCourseAccess(data || { allowed: false });
      } catch (err) {
        // Non-blocking: treat as not allowed
        console.error("Failed to load course access:", err);
        if (isMounted) setCourseAccess({ allowed: false });
      }
    };

    loadAccess();
    return () => {
      isMounted = false;
    };
  }, [courseId, user?.token, user?.userId, loggedInUser?.id, loggedInUser?._id]);

  // Compute completion percent for the first N (free) chapters to enable progressive unlock.
  useEffect(() => {
    let isMounted = true;

    const loadProgress = async () => {
      if (!chapters || chapters.length === 0) {
        if (isMounted) setChapterCompletionPercents([]);
        return;
      }

      // Paid/admin users don't need progress gating.
      if (isAdmin || Boolean(courseAccess?.allowed)) {
        if (isMounted) setChapterCompletionPercents([]);
        return;
      }

      if (!user?.token) {
        if (isMounted) setChapterCompletionPercents([]);
        return;
      }

      try {
        const freeChapters = chapters.slice(0, FREE_CHAPTER_COUNT);
        const percents = await Promise.all(
          freeChapters.map((c) =>
            computeChapterCompletionPercent({
              chapterId: c?.id || c?._id,
              token: user.token,
            }).catch(() => 0)
          )
        );

        const next = new Array(chapters.length).fill(0);
        for (let i = 0; i < percents.length; i += 1) {
          next[i] = percents[i];
        }

        if (isMounted) setChapterCompletionPercents(next);
      } catch (err) {
        console.error("Failed to compute chapter progress:", err);
        if (isMounted) setChapterCompletionPercents([]);
      }
    };

    loadProgress();
    return () => {
      isMounted = false;
    };
  }, [chapters, user?.token, courseAccess?.allowed, isAdmin]);

  const handleDragStart = (index) => (e) => {
    setDraggedChapterIndex(index);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    } catch {
      // no-op
    }
  };

  const handleDragOver = (overIndex) => (e) => {
    if (!isAdmin) return;
    e.preventDefault();
    if (draggedChapterIndex == null || draggedChapterIndex === overIndex) return;

    setChapters((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedChapterIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDraggedChapterIndex(overIndex);
  };

  const getChapterId = (c) => String(c?.id || c?._id || "");

  const hasOrderChanges = (() => {
    if (!chaptersOriginal || chaptersOriginal.length === 0) return false;
    if (chapters.length !== chaptersOriginal.length) return true;
    for (let i = 0; i < chapters.length; i += 1) {
      if (getChapterId(chapters[i]) !== getChapterId(chaptersOriginal[i])) return true;
    }
    return false;
  })();

  const buildChapterOrders = (current) => {
    const orders = {};
    current.forEach((c, idx) => {
      const id = c?.id || c?._id;
      if (id) orders[String(id)] = idx;
    });
    return orders;
  };

  const handleDragEnd = () => {
    if (!isAdmin) return;
    setDraggedChapterIndex(null);
  };

  const handleSaveOrder = async () => {
    if (!isAdmin) return;
    if (!user?.token) return;
    if (!hasOrderChanges) return;
    if (chapters.length <= 1) return;

    const orders = buildChapterOrders(chapters);

    setIsReordering(true);
    try {
      await reorderChapters(orders, user.token);
      successToast("Chapter order saved");
      setChaptersOriginal(chapters);
    } catch (err) {
      console.error("Error reordering chapters:", err);
      errorToast(err?.response?.data?.error || "Failed to reorder chapters");
    } finally {
      setIsReordering(false);
    }
  };

  const handleDiscardOrder = () => {
    if (!isAdmin) return;
    setDraggedChapterIndex(null);
    setChapters(chaptersOriginal);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonCourseDetails />
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaBookOpen className="w-10 h-10 text-neutral-400 dark:text-neutral-500" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
            Course not found
          </h2>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 font-semibold transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
            Go back to courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
      <SeoMeta
        title={`${course.title} - Code Arena`}
        description={course.description}
        url={`https://www.code-arena.in/courses/${courseId}`}
        image="/logo192.png"
        keywords={`${course.title}, golang course, learn golang, coding bootcamp, code arena`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.description,
          provider: {
            "@type": "Organization",
            name: "Code Arena",
            url: "https://www.code-arena.in",
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: "online",
            courseWorkload: "PT10H",
          },
        }}
      />

      {/* Hero Section */}
      <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} py-12 overflow-hidden`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400/20 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate("/courses")}
            className="flex items-center gap-2 text-white/80 hover:text-white font-medium mb-6 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" /> Back to Courses
          </button>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                {course.isPremium && (
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm">
                    <FaCrown className="w-3 h-3" /> Premium
                  </span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {course.title}
              </h1>
              <p className="text-lg text-white/80 max-w-2xl">{course.description}</p>
            </div>

            {/* Admin Actions */}
            {isAdmin && (
              <div className="flex gap-3 flex-wrap">
                <Link
                  to={`/admin/purchases/new?courseId=${courseId}`}
                  className="inline-flex items-center gap-2 bg-white text-accent-600 font-semibold py-2.5 px-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                  title="Grant this course to a user"
                >
                  <FaPlus className="w-4 h-4" /> Grant Access
                </Link>
                <Link
                  to={`/courses/edit/${courseId}`}
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium py-2.5 px-5 rounded-xl hover:bg-white/20 transition-all duration-300 text-sm"
                >
                  <FaEdit className="w-4 h-4" /> Edit
                </Link>
                <Link
                  to={`/courses/${courseId}/chapters/new`}
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium py-2.5 px-5 rounded-xl hover:bg-white/20 transition-all duration-300 text-sm"
                >
                  <FaPlus className="w-4 h-4" /> Add Chapter
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Chapters Section */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                <FaBookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Chapters</h2>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">{chapters.length} chapter{chapters.length !== 1 ? 's' : ''} available</p>
              </div>
            </div>
            {isAdmin && chapters.length > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveOrder}
                  disabled={isReordering || !hasOrderChanges}
                  className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} text-white font-medium py-2 px-4 rounded-xl text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg ${currentTheme.colors.glow}`}
                  title={hasOrderChanges ? "Save chapter order" : "No order changes"}
                >
                  {isReordering ? "Saving..." : "Save Order"}
                </button>
                <button
                  type="button"
                  onClick={handleDiscardOrder}
                  disabled={isReordering || !hasOrderChanges}
                  className="inline-flex items-center gap-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 font-medium py-2 px-4 rounded-xl text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title={hasOrderChanges ? "Discard changes" : "No order changes"}
                >
                  Discard
                </button>
              </div>
            )}
          </div>

          {chapters.length === 0 ? (
            <div className="glass rounded-2xl border border-white/20 dark:border-white/10 p-12 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBookOpen className="w-8 h-8 text-neutral-400 dark:text-neutral-500" />
              </div>
              <p className="text-neutral-600 dark:text-neutral-300 mb-6">No chapters yet</p>
              {loggedInUser?.role === "admin" && (
                <Link
                  to={`/courses/${courseId}/chapters/new`}
                  className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 text-white font-semibold py-3 px-6 rounded-xl shadow-lg ${currentTheme.colors.glow} transition-all`}
                >
                  <FaPlus className="w-4 h-4" /> Create First Chapter
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {chapters.map((chapter, index) => (
                <div
                  key={chapter?.id || chapter?._id || `${index}`}
                  onDragOver={handleDragOver(index)}
                  onDrop={(e) => {
                    if (!isAdmin) return;
                    e.preventDefault();
                  }}
                  className={`glass rounded-2xl border border-white/20 dark:border-white/10 overflow-hidden transition-all duration-300 hover:shadow-lg group ${
                    draggedChapterIndex === index ? "opacity-60" : ""
                  }`}
                >
                  {/* Chapter number indicator */}
                  <div className={`h-1 bg-gradient-to-r ${currentTheme.colors.gradient}`}></div>
                  
                  <div className="p-6">
                  {(() => {
                    const unlockStatus = getChapterUnlockStatus({
                      chapterIndex: index,
                      isPaidUser: Boolean(courseAccess?.allowed),
                      isAdmin: Boolean(isAdmin),
                      chapterCompletionPercents,
                    });

                    const isLocked = !unlockStatus.unlocked;
                    const lockTitle = isLocked ? unlockStatus.reason : "";

                    return (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                            isLocked 
                              ? "bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400" 
                              : "bg-gradient-to-r from-accent-50 to-emerald-50 dark:from-accent-900/30 dark:to-emerald-900/30 text-accent-700 dark:text-accent-300"
                          }`}>
                            {isLocked ? (
                              <>
                                <FaLock className="w-3 h-3" /> Locked
                              </>
                            ) : (
                              <>
                                <FaCheck className="w-3 h-3" /> Chapter {index + 1}
                              </>
                            )}
                          </span>
                          {isAdmin && (
                            <div
                              draggable
                              onDragStart={handleDragStart(index)}
                              onDragEnd={handleDragEnd}
                              className="text-neutral-400 dark:text-neutral-500 cursor-grab active:cursor-grabbing hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                              title="Drag to reorder"
                            >
                              <FaGripVertical className="w-4 h-4" />
                            </div>
                          )}
                        </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold text-neutral-900 dark:text-white mb-2 ${isLocked ? "" : "group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors"}`}>
                        {chapter.title}
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-5 line-clamp-2">
                        {chapter.description}
                      </p>

                      <div className="flex gap-3">
                        {isLocked ? (
                          <button
                            type="button"
                            disabled
                            title={lockTitle}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed"
                          >
                            <FaLock className="w-3.5 h-3.5" /> Locked
                          </button>
                        ) : (
                          <Link
                            to={`/chapter/${chapter.id}`}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-md ${currentTheme.colors.glow} hover:shadow-lg transition-all duration-300 group/btn`}
                          >
                            View Topics
                            <FaArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                          </Link>
                        )}

                        {isAdmin && (
                          <>
                            <Link
                              to={`/chapters/edit/${chapter.id}`}
                              className="flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-50 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                            >
                              <FaEdit className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => handleDeleteChapter(chapter.id)}
                              disabled={deletingChapter === chapter.id}
                              className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                      </>
                    );
                  })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
